import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLearnSectionPayload,
  CreateLearnTopicPayload,
  LearnBlockGroup,
  LearnImageUploadResponse,
  LearnLegalStatus,
  LearnSection,
  LearnTopic,
  MarkdownPreviewResponse,
  SchemaEnsureResponse,
  SeedCatalogResponse,
  UpdateLearnSectionPayload,
  UpdateLearnTopicPayload,
} from '@/client/types/learn';
import { learnKeys } from './learn.query';

const LEARN = '/api/admin/learn';
const TIMEOUT = { timeout: 30_000 };

const unwrapEnvelope = (raw: any): any => {
  if (raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw) {
    return raw.data;
  }
  return raw;
};

// ─── Topics Mutations ────────────────────────────────────────────────────────

export const useCreateLearnTopicMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: CreateLearnTopicPayload): Promise<LearnTopic> => {
    const raw = await request<any>(`${LEARN}/topics`, 'POST', payload, TIMEOUT);
    return unwrapEnvelope(raw);
  }, 'Failed to create Learn topic');

  return useMutation({
    mutationFn: (payload: CreateLearnTopicPayload) => fn(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
    },
  });
};

export const useUpdateLearnTopicMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ id, ...body }: UpdateLearnTopicPayload): Promise<LearnTopic> => {
      const raw = await request<any>(
        `${LEARN}/topics/${encodeURIComponent(id)}`,
        'PATCH',
        body,
        TIMEOUT
      );
      return unwrapEnvelope(raw);
    },
    'Failed to update Learn topic'
  );

  return useMutation({
    mutationFn: (payload: UpdateLearnTopicPayload) => fn(payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.topic(id) });
    },
  });
};

export const useDeleteLearnTopicMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ id, hard = false }: { id: string; hard?: boolean }) => {
      const raw = await request<any>(
        `${LEARN}/topics/${encodeURIComponent(id)}${hard ? '?hard=true' : ''}`,
        'DELETE',
        {},
        TIMEOUT
      );
      return unwrapEnvelope(raw);
    },
    'Failed to delete Learn topic'
  );

  return useMutation({
    mutationFn: (params: { id: string; hard?: boolean }) => fn(params),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.topic(id) });
    },
  });
};

export const usePublishLearnTopicMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (id: string): Promise<LearnTopic> => {
    const raw = await request<any>(
      `${LEARN}/topics/${encodeURIComponent(id)}/publish`,
      'POST',
      {},
      TIMEOUT
    );
    return unwrapEnvelope(raw);
  }, 'Failed to publish Learn topic');

  return useMutation({
    mutationFn: (id: string) => fn(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.topic(id) });
    },
  });
};

export const useUnpublishLearnTopicMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (id: string): Promise<LearnTopic> => {
    const raw = await request<any>(
      `${LEARN}/topics/${encodeURIComponent(id)}/unpublish`,
      'POST',
      {},
      TIMEOUT
    );
    return unwrapEnvelope(raw);
  }, 'Failed to unpublish Learn topic');

  return useMutation({
    mutationFn: (id: string) => fn(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.topic(id) });
    },
  });
};

export const useReorderLearnTopicsMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (order: string[]): Promise<any> => {
    const raw = await request<any>(
      `${LEARN}/topics/reorder`,
      'PUT',
      { order },
      TIMEOUT
    );
    return unwrapEnvelope(raw);
  }, 'Failed to reorder Learn topics');

  return useMutation({
    mutationFn: (order: string[]) => fn(order),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
    },
  });
};

export const useReorderLearnSectionsMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ topicId, order }: { topicId: string; order: string[] }): Promise<any> => {
      const raw = await request<any>(
        `${LEARN}/topics/${encodeURIComponent(topicId)}/sections/reorder`,
        'PUT',
        { order },
        TIMEOUT
      );
      return unwrapEnvelope(raw);
    },
    'Failed to reorder Learn sections'
  );

  return useMutation({
    mutationFn: (params: { topicId: string; order: string[] }) => fn(params),
    onSuccess: (_data, { topicId }) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.topic(topicId) });
    },
  });
};

// ─── Sections Mutations ──────────────────────────────────────────────────────

export const useCreateLearnSectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (payload: CreateLearnSectionPayload): Promise<LearnSection> => {
      const raw = await request<any>(`${LEARN}/sections`, 'POST', payload, TIMEOUT);
      return unwrapEnvelope(raw);
    },
    'Failed to create Learn section'
  );

  return useMutation({
    mutationFn: (payload: CreateLearnSectionPayload) => fn(payload),
    onSuccess: (_data, payload) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.topic(payload.topic) });
    },
  });
};

export const useUpdateLearnSectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ slug, ...body }: UpdateLearnSectionPayload): Promise<LearnSection> => {
      const raw = await request<any>(
        `${LEARN}/sections/${encodeURIComponent(slug)}`,
        'PATCH',
        body,
        TIMEOUT
      );
      return unwrapEnvelope(raw);
    },
    'Failed to update Learn section'
  );

  return useMutation({
    mutationFn: (payload: UpdateLearnSectionPayload) => fn(payload),
    onSuccess: (data, { slug, topic }) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.section(slug) });
      if (data?.slug && data.slug !== slug) {
        qc.invalidateQueries({ queryKey: learnKeys.section(data.slug) });
      }
      if (topic) {
        qc.invalidateQueries({ queryKey: learnKeys.topic(topic) });
      }
    },
  });
};

export const useDeleteLearnSectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ slug, topicId, hard = false }: { slug: string; topicId?: string; hard?: boolean }) => {
      const raw = await request<any>(
        `${LEARN}/sections/${encodeURIComponent(slug)}${hard ? '?hard=true' : ''}`,
        'DELETE',
        {},
        TIMEOUT
      );
      return unwrapEnvelope(raw);
    },
    'Failed to delete Learn section'
  );

  return useMutation({
    mutationFn: (params: { slug: string; topicId?: string; hard?: boolean }) => fn(params),
    onSuccess: (_data, { slug, topicId }) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.section(slug) });
      if (topicId) {
        qc.invalidateQueries({ queryKey: learnKeys.topic(topicId) });
      }
    },
  });
};

export const usePublishLearnSectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (slug: string): Promise<LearnSection> => {
    const raw = await request<any>(
      `${LEARN}/sections/${encodeURIComponent(slug)}/publish`,
      'POST',
      {},
      TIMEOUT
    );
    return unwrapEnvelope(raw);
  }, 'Failed to publish Learn section');

  return useMutation({
    mutationFn: (slug: string) => fn(slug),
    onSuccess: (data, slug) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.section(slug) });
      if (data?.topic?.id) {
        qc.invalidateQueries({ queryKey: learnKeys.topic(data.topic.id) });
      }
    },
  });
};

export const useUnpublishLearnSectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (slug: string): Promise<LearnSection> => {
    const raw = await request<any>(
      `${LEARN}/sections/${encodeURIComponent(slug)}/unpublish`,
      'POST',
      {},
      TIMEOUT
    );
    return unwrapEnvelope(raw);
  }, 'Failed to unpublish Learn section');

  return useMutation({
    mutationFn: (slug: string) => fn(slug),
    onSuccess: (data, slug) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.section(slug) });
      if (data?.topic?.id) {
        qc.invalidateQueries({ queryKey: learnKeys.topic(data.topic.id) });
      }
    },
  });
};

/** Store a photo against a section; returns the url to place in the markdown. */
export const useUploadLearnSectionImageMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async ({ slug, file, credit }: { slug: string; file: File; credit: string }): Promise<LearnImageUploadResponse> => {
      const form = new FormData();
      form.append('file', file, file.name);
      form.append('credit', credit);
      const raw = await request<any>(
        `${LEARN}/sections/${encodeURIComponent(slug)}/images`,
        'POST',
        form,
        { timeout: 60_000 }
      );
      return unwrapEnvelope(raw);
    },
    'Failed to upload photo'
  );

  return useMutation({
    mutationFn: (p: { slug: string; file: File; credit: string }) => fn(p),
  });
};

/** Record a legal/policy decision. Only `cleared` unblocks publishing. */
export const useLearnLegalReviewMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ slug, status, notes }: { slug: string; status: LearnLegalStatus; notes?: string }) => {
      const raw = await request<any>(
        `${LEARN}/sections/${encodeURIComponent(slug)}/legal-review`,
        'POST',
        { status, notes },
        TIMEOUT
      );
      return unwrapEnvelope(raw);
    },
    'Failed to record legal review'
  );

  return useMutation({
    mutationFn: (p: { slug: string; status: LearnLegalStatus; notes?: string }) => fn(p),
    onSuccess: (_data, { slug }) => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
      qc.invalidateQueries({ queryKey: learnKeys.section(slug) });
    },
  });
};

// ─── Markdown Preview & Bootstrap Mutations ──────────────────────────────────

export const useAdminLearnMarkdownPreviewMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (markdown: string): Promise<LearnBlockGroup[]> => {
      const raw = await request<any>(
        `${LEARN}/preview/markdown`,
        'POST',
        { markdown },
        TIMEOUT
      );
      const payload: MarkdownPreviewResponse = unwrapEnvelope(raw);
      return payload?.blocks ?? [];
    },
    'Failed to preview markdown'
  );

  return useMutation({
    mutationFn: (markdown: string) => fn(markdown),
  });
};

export const useEnsureLearnSchemaMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (): Promise<SchemaEnsureResponse> => {
    const raw = await request<any>(`${LEARN}/schema/ensure`, 'POST', {}, TIMEOUT);
    return unwrapEnvelope(raw);
  }, 'Failed to ensure Learn schema');

  return useMutation({
    mutationFn: () => fn(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
    },
  });
};

export const useSeedLearnCatalogMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (publish = false): Promise<SeedCatalogResponse> => {
      const raw = await request<any>(
        `${LEARN}/seed`,
        'POST',
        { publish },
        TIMEOUT
      );
      return unwrapEnvelope(raw);
    },
    'Failed to seed Learn catalog'
  );

  return useMutation({
    mutationFn: (publish?: boolean) => fn(publish ?? false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: learnKeys.all });
    },
  });
};
